import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl, type VenueInput, type UpdateVenueRequest } from "@shared/routes";

export type MapBounds = {
  latMin: number;
  latMax: number;
  lngMin: number;
  lngMax: number;
};

const snapBounds = (bounds: MapBounds | null, precision = 0.02) => {
  if (!bounds) return null;
  const snap = (v: number) => Math.round(v / precision) * precision;
  return {
    latMin: snap(bounds.latMin) - precision,
    latMax: snap(bounds.latMax) + precision,
    lngMin: snap(bounds.lngMin) - precision,
    lngMax: snap(bounds.lngMax) + precision,
  };
};

export function useVenues(
  mapBounds: MapBounds | null,
  includeClosed = false,
  searchQuery = "",
  userLocation?: { lat: number; lng: number } | null,
) {
  const trimmedSearch = searchQuery.trim();
  const hasSearch = trimmedSearch.length >= 2;
  const snappedBounds = snapBounds(mapBounds);
  // Snap user location to a coarse grid so small marker drift doesn't bust the
  // query cache key on every re-render.
  const snappedLoc = userLocation
    ? { lat: Math.round(userLocation.lat / 0.01) * 0.01, lng: Math.round(userLocation.lng / 0.01) * 0.01 }
    : null;
  const queryClient = useQueryClient();
  const queryResult = useQuery({
    queryKey: hasSearch
      ? ["venues", "search", trimmedSearch, includeClosed, snappedLoc]
      : ["venues", snappedBounds, includeClosed],
    queryFn: async () => {
      if (hasSearch) {
        const url = new URL("/api/venues/search", window.location.origin);
        url.searchParams.set("q", trimmedSearch);
        if (includeClosed) url.searchParams.set("includeClosed", "true");
        if (userLocation) {
          url.searchParams.set("lat", String(userLocation.lat));
          url.searchParams.set("lng", String(userLocation.lng));
        }
        const res = await fetch(url.toString(), { credentials: "include" });
        if (!res.ok) throw new Error("Failed to search venues");
        const data = await res.json();
        return data.venues as any[];
      }
      if (!snappedBounds) return [];
      const url = new URL("/api/venues", window.location.origin);
      url.searchParams.set("latMin", String(snappedBounds.latMin));
      url.searchParams.set("latMax", String(snappedBounds.latMax));
      url.searchParams.set("lngMin", String(snappedBounds.lngMin));
      url.searchParams.set("lngMax", String(snappedBounds.lngMax));
      url.searchParams.set("limit", "500");
      if (includeClosed) url.searchParams.set("includeClosed", "true");
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch venues");
      const data = await res.json();
      return data.venues as any[];
    },
    enabled: hasSearch || mapBounds !== null,
    placeholderData: (previousData: any[] | undefined) => previousData,
    refetchInterval: hasSearch ? false : 1800000,
    staleTime: hasSearch ? 0 : 1800000,
    gcTime: hasSearch ? 0 : undefined,
    refetchOnWindowFocus: hasSearch ? true : undefined,
  });

  const { dataUpdatedAt } = queryResult;
  useEffect(() => {
    if (!hasSearch && dataUpdatedAt) {
      queryClient.invalidateQueries({ queryKey: ["venues", "search"] });
    }
  }, [dataUpdatedAt]);

  return queryResult;
}

// All currently sponsored venues (no viewport bounds, no limit). Used so Honey markers
// stay visible at any zoom level even when the bounds-scoped /api/venues LIMIT 500
// would exclude them.
export function useSponsoredVenues() {
  return useQuery({
    queryKey: ["/api/venues/sponsored"],
    queryFn: async () => {
      const res = await fetch("/api/venues/sponsored", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch sponsored venues");
      const data = await res.json();
      return data.venues as any[];
    },
    refetchInterval: 1800000,
    staleTime: 1800000,
  });
}

// City-wide, viewport-independent dataset that feeds the map heatmap. The query
// key has NO map bounds, so panning/zooming never refetches it — the heat bakes
// once from a steady dataset and only auto-refreshes on the 30-minute timer
// (matching the live-capacity refresh cadence).
export function useHeatVenues() {
  return useQuery({
    queryKey: ["/api/venues/heat"],
    queryFn: async () => {
      const res = await fetch("/api/venues/heat", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch heat venues");
      const data = await res.json();
      return data.venues as any[];
    },
    refetchInterval: 1800000,
    staleTime: 1800000,
  });
}

export function useVenue(id: number) {
  return useQuery({
    queryKey: [api.venues.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.venues.get.path, { id });
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Failed to fetch venue');
      return api.venues.get.responses[200].parse(await res.json());
    },
    enabled: id > 0,
  });
}

export function useCreateVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: VenueInput) => {
      const validated = api.venues.create.input.parse(data);
      const res = await fetch(api.venues.create.path, {
        method: api.venues.create.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(validated),
        credentials: "include",
      });
      if (!res.ok) {
        if (res.status === 400) {
          const error = api.venues.create.responses[400].parse(await res.json());
          throw new Error(error.message);
        }
        throw new Error('Failed to create venue');
      }
      return api.venues.create.responses[201].parse(await res.json());
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.venues.list.path] }),
  });
}

export function useUpdateVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: number } & UpdateVenueRequest) => {
      const validated = api.venues.update.input.parse(updates);
      const url = buildUrl(api.venues.update.path, { id });
      const res = await fetch(url, {
        method: api.venues.update.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(validated),
        credentials: "include",
      });
      if (!res.ok) {
        if (res.status === 400) {
          const error = api.venues.update.responses[400].parse(await res.json());
          throw new Error(error.message);
        }
        throw new Error('Failed to update venue');
      }
      return api.venues.update.responses[200].parse(await res.json());
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.venues.list.path] }),
  });
}
